import { ICommonSettings, IGoogleMapsSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigGoogleMapsSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingMaps?: IGoogleMapsSetting;
}

export interface IConfigGoogleMapsSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingMapsId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigGoogleMapsSettings extends React.PureComponent<
  IConfigGoogleMapsSettingsProps,
  IConfigGoogleMapsSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigGoogleMapsSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadMapsSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigGoogleMapsSettingsProps) {
    if (prevProps.editingMapsId !== this.props.editingMapsId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadMapsSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadMapsSetting();
    }
  }

  loadMapsSetting = () => {
    if (!this.props.editingMapsId) return;

    const editingMaps = this.props.commonSettings.googleMapsSettings?.find(
      (x) => x.id === this.props.editingMapsId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingMaps) return;
    this.setState({ editingMaps }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingMaps);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const mapssSettings = this.props.commonSettings?.googleMapsSettings;

    const isSettingAlreadyExist =
      mapssSettings?.some(
        (x) => x.name === value && x.id !== this.props.editingMapsId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Google Maps Settings Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();

    let obj: IGoogleMapsSetting;
    if (this.props.editingMapsId) {
      obj = {
        id: this.props.editingMapsId,
        name: data.name,
        apiKey: data.apiKey
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleMapsSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.name,
        apiKey: data.apiKey?.trim()
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleMapsSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteMaps = () => {
    if (!this.props.editingMapsId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    const parentDataKey = this.props.formInputName[0] === "webConfig" ? "androidConfig" : "webConfig";

    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingMapsId || "",
      configKey: "googleMapsSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Google Maps setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      shouldControlOtherSections: {
        listOfControl: [[parentDataKey, this.props.formInputName[1]]],
        onCannotDelete: () => {
          KMainFunctions.displayErrorNotification(`
          This Google Maps setting is
          used on ${
            this.props.formInputName[0] === "webConfig" ? "Android" : "Web"
          } section. Please discard this setting
          on other config section before delete.
        `);
        }
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Google Maps Setting"
        secondHeader="Google Maps Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingMapsId ? this.deleteMaps : undefined}
            deleteTestId="googleMapsSettingDeleteButton"
            submitTestId={`googleMapsSetting${this.props.editingMapsId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingMapsId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configGoogleMapsForm"
          />
        }
      >
        <Form
          id="configGoogleMapsForm"
          name="mapsSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("googlemapssettingspopup_googlemapssettings_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="googlemapssettingspopup_googlemapssettings_name">
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter a Google Maps Setting Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Google Maps Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="googleMapsSettingsNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Api Key" required tooltipContentKey="googlemapssettingspopup_googlemapssettings_apikey">
              <Form.Item name="apiKey" rules={[{ required: true, message: "Please enter a Api Key." }]}>
                <KuikaInput size="md" data-testid="googleMapsSettingApiKeyInput" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
